import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { NumberBall } from "@/components/NumberBall";
import { SEOHead } from "@/components/SEOHead";
import { Calendar, Target, TrendingUp, Flame, Snowflake, Clock, Award } from "lucide-react";
import { LotterySubpagesLinks, RelatedLotteriesLinks, BreadcrumbNav } from "@/components/InternalLinksSEO";
import { LotteryKPIDisplay } from "@/components/LotteryKPIDisplay";
import WinningOdds from "@/components/WinningOdds";

import { API } from "@/config/api";

const MegaSenaPage = () => {
  const { lang } = useParams();

  const [latestDraw, setLatestDraw] = useState(null);
  const [nextDraw, setNextDraw] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [recentDraws, setRecentDraws] = useState([]);
  const [kpiV2Data, setKpiV2Data] = useState(null);
  const [loading, setLoading] = useState(true);

  const safeFormatDate = (rawDate) => {
    if (!rawDate) return "N/A";
    const d = new Date(rawDate);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return null;
    return `R$ ${amount.toLocaleString('pt-BR')}`;
  };

  useEffect(() => {
    const load = async () => {
      try {
        const results = await Promise.allSettled([
          axios.get(`${API}/lottery/megasena/latest`),
          axios.get(`${API}/lottery/megasena/next`),
          axios.get(`${API}/lottery/megasena/predictions`),
          axios.get(`${API}/lotteries/megasena/stats?window=50`),
          axios.get(`${API}/lotteries/megasena/draws?limit=10`),
          axios.get(`${API}/kpi-v2/megasena`),
        ]);

        const [latest, next, preds, stats, draws, kpiV2] = results.map((r) =>
          r.status === "fulfilled" ? r.value.data : null
        );

        setLatestDraw(latest);
        setNextDraw(next);
        setPredictions(preds);
        setStatistics(stats);
        setRecentDraws(draws?.draws || []);
        setKpiV2Data(kpiV2);
      } catch (e) {
        console.error("MegaSena load error:", e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen py-6 text-center text-white">
        Loading...
      </div>
    );
  }

  // Extract hot/cold/overdue from statistics
  const hotNumbers = statistics?.hot_numbers || [];
  const coldNumbers = statistics?.cold_numbers || [];
  const overdueNumbers = statistics?.overdue_numbers || [];

  // Format PI as multiplier
  const formatPI = (pi) => {
    if (pi === null || pi === undefined) return 'N/A';
    return `${pi.toFixed(2)}×`;
  };

  return (
    <div className="min-h-screen py-4">
      <SEOHead title="Mega-Sena" path="/lottery/megasena" />

      <div className="max-w-7xl mx-auto px-4">
        {/* BREADCRUMB NAV - SEO */}
        <BreadcrumbNav 
          items={[{ label: 'Mega-Sena' }]} 
          className="mb-3"
        />
        
        {/* HEADER */}
        <div className="mb-4">
          <h1 className="text-3xl font-bold text-white mb-1">Mega-Sena 🇧🇷</h1>
          <p className="text-gray-400 text-sm">
            Draws: Tuesday, Thursday & Saturday | Jackpot starts at R$ 3M
          </p>

          <div className="flex gap-2 mt-3">
            <Link to={`/${lang}/predictions?lottery=megasena`}>
              <Button size="sm" className="bg-amber-500 hover:bg-amber-600">
                <Target className="w-3 h-3 mr-1" />
                Generate Prediction
              </Button>
            </Link>

            <Link to={`/${lang}/statistics?lottery=megasena`}>
              <Button
                size="sm"
                variant="outline"
                className="border-gray-700 text-white hover:bg-gray-800"
              >
                <TrendingUp className="w-3 h-3 mr-1" />
                View Statistics
              </Button>
            </Link>
          </div>
          
          {/* SUBPAGES NAV - SEO Cross-linking */}
          <LotterySubpagesLinks 
            lotteryCode="megasena" 
            lotteryName="Mega-Sena"
            className="mt-4"
          />
        </div>

        {/* MAIN LAYOUT - 3 columns for content + 1 column for Performance/Ad */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3.5 mb-3">
          
          {/* LEFT SECTION - Columns 1-3 */}
          <div className="lg:col-span-3 space-y-3">
            
            {/* TOP ROW: Latest Draw / Next Draw / Predictions */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
              
              {/* COLUMN 1: LATEST DRAW - Compact */}
              <Card className="bg-gray-900/40 border-gray-700/50">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-white text-sm">Latest Draw</CardTitle>
                  <p className="text-[10px] text-gray-400">
                    {safeFormatDate(latestDraw?.date)}
                  </p>
                </CardHeader>

                <CardContent className="pt-0 pb-2 px-3">
                  {latestDraw?.main_numbers?.length ? (
                    <div className="flex flex-wrap gap-1">
                      {latestDraw.main_numbers.map((n, i) => (
                        <NumberBall key={i} number={n} variant="main" size="xs" />
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-xs">No recent draws</p>
                  )}
                </CardContent>
              </Card>

              {/* COLUMN 2: NEXT DRAW - Compact */}
              <Card className="bg-gray-900/40 border-gray-700/50">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-white text-sm flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Next Draw
                  </CardTitle>
                </CardHeader>

                <CardContent className="pt-0 pb-2 px-3 text-white">
                  <p className="text-base font-bold">
                    {safeFormatDate(nextDraw?.next_draw_date)}
                  </p>
                  {nextDraw?.next_jackpot && (
                    <p className="text-[10px] text-gray-400">
                      {formatCurrency(nextDraw.next_jackpot)}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* COLUMN 3: PREDICTIONS - Compact */}
              <Card className="bg-gray-900/40 border-gray-700/50">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-white text-sm">Predictions</CardTitle>
                </CardHeader>

                <CardContent className="pt-0 pb-2 px-3">
                  {predictions?.current_predictions?.length > 0 ? (
                    <div className="space-y-1.5">
                      {/* Statistical */}
                      <div>
                        <h4 className="text-[9px] font-semibold text-blue-400 mb-0.5">📊 Statistical</h4>
                        {(() => {
                          const p = predictions.current_predictions.find((x) => x.model === "statistical");
                          if (!p) return null;

                          return (
                            <div className="flex flex-wrap gap-0.5">
                              {p.main_numbers?.slice(0, 6).map((n, i) => (
                                <NumberBall key={i} number={n} variant="main" size="xs" />
                              ))}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Lotterics */}
                      <div>
                        <h4 className="text-[9px] font-semibold text-amber-400 mb-0.5">🔥 Lotterics</h4>
                        {(() => {
                          const p = predictions.current_predictions.find((x) => x.model === "lotteryx");
                          if (!p) return null;

                          return (
                            <div className="flex flex-wrap gap-0.5">
                              {p.main_numbers?.slice(0, 6).map((n, i) => (
                                <NumberBall key={i} number={n} variant="main" size="xs" />
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-400 text-xs">No predictions available</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* SECOND ROW: Hot / Cold / Overdue */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
              
              {/* HOT NUMBERS */}
              <Card className="bg-gray-900/40 border-gray-700/50">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-white text-sm flex items-center gap-2">
                    <Flame className="w-4 h-4 text-red-400" />
                    Hot Numbers
                  </CardTitle>
                  <p className="text-[9px] text-gray-500 mt-0.5">Most frequent in last 50 draws</p>
                </CardHeader>
                <CardContent className="pt-0 pb-2 px-3">
                  {hotNumbers.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {hotNumbers.slice(0, 6).map((n, i) => (
                        <NumberBall key={i} number={n} variant="hot" size="xs" />
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No data</p>
                  )}
                </CardContent>
              </Card>

              {/* COLD NUMBERS */}
              <Card className="bg-gray-900/40 border-gray-700/50">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-white text-sm flex items-center gap-2">
                    <Snowflake className="w-4 h-4 text-blue-400" />
                    Cold Numbers
                  </CardTitle>
                  <p className="text-[9px] text-gray-500 mt-0.5">Least frequent in last 50 draws</p>
                </CardHeader>
                <CardContent className="pt-0 pb-2 px-3">
                  {coldNumbers.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {coldNumbers.slice(0, 6).map((n, i) => (
                        <NumberBall key={i} number={n} variant="cold" size="xs" />
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No data</p>
                  )}
                </CardContent>
              </Card>

              {/* OVERDUE NUMBERS */}
              <Card className="bg-gray-900/40 border-gray-700/50">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-white text-sm flex items-center gap-2">
                    <Clock className="w-4 h-4 text-purple-400" />
                    Overdue Numbers
                  </CardTitle>
                  <p className="text-[9px] text-gray-500 mt-0.5">Longest since last appearance</p>
                </CardHeader>
                <CardContent className="pt-0 pb-2 px-3">
                  {overdueNumbers.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {overdueNumbers.slice(0, 6).map((item, i) => (
                        <NumberBall key={i} number={typeof item === 'object' ? item.number : item} variant="main" size="xs" />
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No data</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* RECENT 10 DRAWS - Inside left section */}
            <Card className="bg-gray-900/40 border-gray-700/50">
              <CardHeader className="py-2 px-3">
                <CardTitle className="text-white text-sm">Recent 10 Draws</CardTitle>
              </CardHeader>

              <CardContent className="pt-0 pb-2 px-3 overflow-x-auto">
                {recentDraws.length ? (
                  <table className="w-full text-xs">
                    <tbody>
                      {recentDraws.map((d, idx) => (
                        <tr key={d.id || idx} className="border-b border-gray-700/30 last:border-0">
                          <td className="py-1.5 text-gray-300 w-24">
                            {safeFormatDate(d.draw_date || d.date)}
                          </td>
                          <td className="py-1.5">
                            <div className="flex flex-wrap gap-1">
                              {d.main_numbers.map((n, i) => (
                                <NumberBall key={i} number={n} variant="main" size="xs" />
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-xs text-gray-400">No recent draws</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* RIGHT SECTION - Column 4: Performance + Advertisement */}
          <div className="space-y-3">
            {/* KPI v2 Display */}
            <LotteryKPIDisplay 
              kpiData={kpiV2Data} 
              loading={false}
              className="bg-gray-900/40 border-gray-700/50"
            />

            {/* ADVERTISEMENT - Below Performance */}
            <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
              <div className="w-full h-24 bg-gradient-to-b from-amber-500/10 to-amber-600/5 rounded-lg flex items-center justify-center border border-amber-500/20">
                <div className="text-center">
                  <div className="text-amber-500/60 text-xs font-medium mb-0.5">
                    Advertisement
                  </div>
                  <div className="text-amber-500/40 text-[10px]">300 × 100</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* WINNING ODDS */}
        <WinningOdds lotteryCode="megasena" />
        
        {/* RELATED LOTTERIES - SEO Cross-linking */}
        <div className="mt-8 pt-6 border-t border-gray-800">
          <RelatedLotteriesLinks 
            currentLotteryCode="megasena"
            title="Explore Other Lotteries"
          />
        </div>
      </div>
    </div>
  );
};

export default MegaSenaPage;
